// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — the opponent, and the deterministic filler for a seat whose player has gone.
//
// THE BUDGET IS A NODE COUNT, NEVER A MILLISECOND BUDGET. This search is on the disconnect-autoplay
// path, where every peer independently computes the move for an absent seat and appends it to the
// shared log. A time box returns a different move on a slow phone than on a fast one, so a time-
// boxed search here is not a performance choice — it is a live desync. Nothing in this file reads a
// clock or Math.random; the one stochastic tier draws from a hash of the log.
//
// THE EVALUATION IS TWO-STAGE because a full board analysis per candidate is far too expensive to
// run inside the balance sim. Stage one ranks EVERY legal placement by its EXACT change in realised
// score, which the region map gives for free; stage two runs the structural terms — parity debt,
// fracture, fragmentation, flexibility — on only the best handful. The exactness of stage one is
// what makes the cut safe: it is a real ordering, not a heuristic pre-filter.
//
// THE ONE LINE THAT MATTERS MOST is ORDER_W ramping to zero on the final round. Claiming a high
// number buys a late slot NEXT round, so on the last round it costs nothing at all and the correct
// play is a pure grab. A bot that keeps paying tempo it can never spend plays visibly badly at
// exactly the moment a human is paying attention.

import { doubletOf, TERRAIN_COUNT, type Doublet } from './deck';
import {
  EMPTY,
  ROTS,
  encodeMove,
  isBurnTurn,
  legalPlacements,
  mate,
  openPicks,
  seatToPlay,
  type Demesne,
  type State,
} from './rules';

export interface Tier {
  id: string;
  name: string;
  blurb: string;
  /** Scales how much the bot is willing to pay for pick order. */
  orderScale: number;
  /** Candidate placements given the full structural evaluation, per claim considered. */
  wide: number;
  /** Hard ceiling on full evaluations in one turn. Deterministic truncation, never a timer. */
  nodeCap: number;
  /** Chance in sixteenths of taking the second-best move, drawn from a hash of the log. */
  noise: number;
  /** Weigh what a doublet would be worth to the best-placed rival. */
  denial: number;
  /** Structural terms at all — Hind plays on realised score and parity alone. */
  structural: boolean;
  /**
   * holt only: what the veto is worth. 0 makes holt price pick order exactly as croft does, 1
   * cancels the tempo price outright, above 1 makes going last actively desirable.
   *
   * MEASURED, AND THE MEASUREMENT REFUTED THE DESIGN. The intent was that holt's burn would make
   * going last worth paying for and invert the game's only dial. It does not: the sweep in
   * tests/balance.test.ts finds the tempo GAP peaks at veto = 0 and goes NEGATIVE above 1, i.e. a
   * bot that pays for the veto plays worse than one that ignores it. Shipped at 0, and holt earns
   * its place as a mode on what the burn actually does — see modes.ts.
   */
  veto: number;
}

/**
 * The weight every shipped tier puts on holt's veto — zero, because that is what measured best.
 * tests/balance.test.ts re-runs the sweep that produced it and fails if some other value wins.
 */
export const VETO_W = 0;

/**
 * THE LADDER IS MEASURED, NOT ASSERTED (tests/balance.test.ts, and the sweeps quoted in the build
 * log). Two things that looked obviously right were measured and dropped: a foresight term testing
 * the board against what is still in the pool changed nothing at all (50.8 vs 51.4 — worse, inside
 * the noise), and raising the denial weight from 0.3 to 0.55 made the bot WORSE by 1.3 points. What
 * actually separates a tier is how much of the evaluation it uses and how often it takes the
 * second-best claim: 2/16 costs 4.0 points, 6/16 costs 6.8, greedy-instead-of-structural costs 4.7.
 * The order weight has a genuine optimum near 0.75 and falls off a cliff above it — at 2.2 the bot
 * loses by TWELVE points, because it spends the whole match paying for a tempo it never cashes.
 */
export const TIERS: Record<string, Tier> = {
  hind: {
    id: 'hind',
    name: 'Hind',
    blurb: 'Takes the coronets in front of it and rarely looks further. A gentle first game.',
    orderScale: 0.25,
    wide: 4,
    nodeCap: 400,
    noise: 6,
    denial: 0,
    structural: false,
    veto: VETO_W,
  },
  reeve: {
    id: 'reeve',
    name: 'Reeve',
    blurb: 'Plays the whole board — shape, spare room and the price of going first. A real opponent.',
    orderScale: 0.5,
    wide: 10,
    nodeCap: 1200,
    noise: 2,
    denial: 0.3,
    structural: true,
    veto: VETO_W,
  },
  hayward: {
    id: 'hayward',
    name: 'Hayward',
    blurb: 'Never wastes a claim, never overpays for the first pick, and takes what you needed.',
    orderScale: 0.75,
    wide: 14,
    nodeCap: 2400,
    noise: 0,
    denial: 0.3,
    structural: true,
    veto: VETO_W,
  },
};

export const TIER_IDS = ['hind', 'reeve', 'hayward'];
export const DEFAULT_TIER = 'reeve';

/**
 * The tier that fills for a disconnected seat, on every peer at once. A hard constant, never a
 * setting: two peers filling the same slot at different strengths would author different moves for
 * the same key, and the room would tear in half.
 *
 * It is the STRONGEST tier and the only one with no noise, because it also plays your own turn when
 * your clock runs out. Being timed out should cost you the turn, not the match.
 */
export const FILL_TIER = 'hayward';

export function tierOf(id: string | null | undefined): Tier {
  if (typeof id === 'string' && Object.hasOwn(TIERS, id)) return TIERS[id];
  return TIERS[DEFAULT_TIER];
}

// ── board analysis ──────────────────────────────────────────────────────────────
interface Analysis {
  /** Region index per cell, -1 for empty or the Motte. */
  regionOf: Int16Array;
  cells: number[];
  coronets: number[];
  terrain: number[];
  /** Empty cells orthogonally adjacent to the region. */
  frontier: number[];
  /** Σ cells × coronets — the realised score. */
  base: number;
  empty: number;
  /** Σ |dark − light| over empty components. Zero iff a perfect fill is still geometrically possible. */
  parity: number;
  /** Empty components beyond the first. */
  fracture: number;
  /** Same-terrain regions beyond the first, summed over terrains. */
  fragments: number;
  /** Single-cell regions carrying a coronet. */
  orphans: number;
  terrains: number;
  /** Empty cells that could legally take terrain t — the flexibility term's raw material. */
  openings: number[];
}

function analyse(d: Demesne, side: number): Analysis {
  const n = side * side;
  const regionOf = new Int16Array(n).fill(-1);
  const cells: number[] = [];
  const coronets: number[] = [];
  const terrain: number[] = [];
  const frontier: number[] = [];
  const stack: number[] = [];
  const perTerrain = new Int8Array(TERRAIN_COUNT);
  const openings = new Array<number>(TERRAIN_COUNT).fill(0);
  let base = 0;
  let empty = 0;
  let orphans = 0;

  for (let i = 0; i < n; i++) {
    if (regionOf[i] >= 0 || d.terr[i] < 0) continue;
    const t = d.terr[i];
    const id = cells.length;
    let size = 0;
    let crowns = 0;
    let edge = 0;
    regionOf[i] = id;
    stack.length = 0;
    stack.push(i);
    while (stack.length > 0) {
      const c = stack.pop() as number;
      size++;
      crowns += d.cor[c];
      const row = (c / side) | 0;
      const col = c % side;
      const nb = [col > 0 ? c - 1 : -1, col < side - 1 ? c + 1 : -1, row > 0 ? c - side : -1, row < side - 1 ? c + side : -1];
      for (const j of nb) {
        if (j < 0) continue;
        if (d.terr[j] === t && regionOf[j] < 0) {
          regionOf[j] = id;
          stack.push(j);
        } else if (d.terr[j] === EMPTY) edge++;
      }
    }
    cells.push(size);
    coronets.push(crowns);
    terrain.push(t);
    frontier.push(edge);
    base += size * crowns;
    perTerrain[t]++;
    if (size === 1 && crowns > 0) orphans++;
  }

  // Openings: an empty cell counts once for each terrain it could legally receive. The Motte makes
  // its four neighbours open to everything, which is exactly why the opening is never a dead end.
  for (let c = 0; c < n; c++) {
    if (d.terr[c] !== EMPTY) continue;
    empty++;
    const row = (c / side) | 0;
    const col = c % side;
    const nb = [col > 0 ? c - 1 : -1, col < side - 1 ? c + 1 : -1, row > 0 ? c - side : -1, row < side - 1 ? c + side : -1];
    let wild = false;
    const seenT = new Int8Array(TERRAIN_COUNT);
    for (const j of nb) {
      if (j < 0) continue;
      if (d.terr[j] < EMPTY) wild = true;
      else if (d.terr[j] >= 0) seenT[d.terr[j]] = 1;
    }
    for (let t = 0; t < TERRAIN_COUNT; t++) if (wild || seenT[t]) openings[t]++;
  }

  // Empty components: parity debt and fracture in one pass.
  const seen = new Uint8Array(n);
  let parity = 0;
  let comps = 0;
  for (let i = 0; i < n; i++) {
    if (seen[i] || d.terr[i] !== EMPTY) continue;
    comps++;
    let bal = 0;
    seen[i] = 1;
    stack.length = 0;
    stack.push(i);
    while (stack.length > 0) {
      const c = stack.pop() as number;
      bal += (((c / side) | 0) + (c % side)) % 2 === 0 ? 1 : -1;
      const row = (c / side) | 0;
      const col = c % side;
      if (col > 0 && !seen[c - 1] && d.terr[c - 1] === EMPTY) {
        seen[c - 1] = 1;
        stack.push(c - 1);
      }
      if (col < side - 1 && !seen[c + 1] && d.terr[c + 1] === EMPTY) {
        seen[c + 1] = 1;
        stack.push(c + 1);
      }
      if (row > 0 && !seen[c - side] && d.terr[c - side] === EMPTY) {
        seen[c - side] = 1;
        stack.push(c - side);
      }
      if (row < side - 1 && !seen[c + side] && d.terr[c + side] === EMPTY) {
        seen[c + side] = 1;
        stack.push(c + side);
      }
    }
    parity += Math.abs(bal);
  }

  let terrains = 0;
  let fragments = 0;
  for (let t = 0; t < TERRAIN_COUNT; t++) {
    if (perTerrain[t] > 0) terrains++;
    if (perTerrain[t] > 1) fragments += perTerrain[t] - 1;
  }

  return {
    regionOf,
    cells,
    coronets,
    terrain,
    frontier,
    base,
    empty,
    parity,
    fracture: Math.max(0, comps - 1),
    fragments,
    orphans,
    terrains,
    openings,
  };
}

/**
 * The EXACT change in realised score from a placement, straight off the region map.
 *
 * A placement merges the two new cells with every same-terrain region they touch, so the delta is
 * (merged cells × merged coronets) summed over the resulting regions, minus what those regions were
 * worth before. Getting this exactly right rather than approximating it is what lets stage one cut
 * eighty candidates down to ten without ever discarding the actual best move.
 */
const HALF_T = [0, 0];
const HALF_C = [0, 0];
const HALF_K = [0, 0];
const IDS = new Int16Array(16);

function gainOf(a: Analysis, d: Demesne, side: number, doub: Doublet, cell: number, rot: number): number {
  const b = mate(side, cell, rot);
  HALF_C[0] = cell;
  HALF_T[0] = doub.a.terrain;
  HALF_K[0] = doub.a.coronets;
  HALF_C[1] = b;
  HALF_T[1] = doub.b.terrain;
  HALF_K[1] = doub.b.coronets;

  // The two halves, plus every same-terrain region they touch, merge into one or two clusters. Two
  // halves of the SAME terrain are one cluster whether or not they touch anything, which is why the
  // grouping is by terrain rather than by half.
  const sameHalves = HALF_T[0] === HALF_T[1];
  const groups = sameHalves ? 1 : 2;
  let after = 0;
  let before = 0;
  for (let g = 0; g < groups; g++) {
    const t = HALF_T[g];
    let cells = sameHalves ? 2 : 1;
    let crowns = sameHalves ? HALF_K[0] + HALF_K[1] : HALF_K[g];
    let nIds = 0;
    for (let h = 0; h < 2; h++) {
      if (!sameHalves && h !== g) continue;
      const c = HALF_C[h];
      const row = (c / side) | 0;
      const col = c % side;
      for (let k = 0; k < 4; k++) {
        const j = k === 0 ? (col > 0 ? c - 1 : -1) : k === 1 ? (col < side - 1 ? c + 1 : -1) : k === 2 ? (row > 0 ? c - side : -1) : row < side - 1 ? c + side : -1;
        if (j < 0 || d.terr[j] !== t) continue;
        const id = a.regionOf[j];
        let dup = false;
        for (let q = 0; q < nIds; q++) if (IDS[q] === id) dup = true;
        if (!dup && nIds < 16) IDS[nIds++] = id;
      }
    }
    for (let q = 0; q < nIds; q++) {
      const id = IDS[q];
      cells += a.cells[id];
      crowns += a.coronets[id];
      before += a.cells[id] * a.coronets[id];
    }
    after += cells * crowns;
  }
  return after - before;
}

function structural(a: Analysis, s: State, doubletsLeft: number, roundsLeft: number): number {
  let v = a.base;
  // growth: a coroneted region with room to expand is worth more than its current points.
  for (let i = 0; i < a.cells.length; i++) {
    if (a.coronets[i] > 0) v += 0.6 * a.coronets[i] * Math.min(a.frontier[i], 3);
    else v += 0.09 * a.cells[i] * Math.min(a.frontier[i], 3);
  }
  // flexibility: somewhere to put each terrain, capped so it cannot dominate realised points.
  let flex = 0;
  for (let t = 0; t < TERRAIN_COUNT; t++) flex += Math.min(3, a.openings[t]);
  v += 0.55 * flex;
  // The two terms that stop it painting itself into a hole. Without them it discards constantly and
  // the game reads as broken rather than hard.
  v -= 2.2 * a.parity;
  v -= 0.4 * a.fracture;
  v -= 0.8 * a.fragments;
  v -= 1.5 * a.orphans;
  v -= 0.9 * (TERRAIN_COUNT - a.terrains) * (roundsLeft <= 4 ? 2 : 0.6);
  if (s.mode.wholefold) {
    const projected = Math.max(0, a.empty - 2 * doubletsLeft) + a.parity;
    v += Math.max(0, 10 - 2 * projected);
  }
  if (s.mode.holdfast) {
    for (const c of a.cells) v += c >= 6 ? 3 : c === 5 ? 0.7 : 0;
  }
  return v;
}

const scratch = (d: Demesne): Demesne => ({ terr: d.terr.slice(), cor: d.cor.slice() });

interface Best {
  value: number;
  cell: number;
  rot: number;
  gain: number;
  legal: boolean;
}

/**
 * The best placement of one doublet on one demesne, and what it is worth.
 *
 * `a` and `baseline` are the acting seat's board analysed ONCE for the whole turn — the board does
 * not change between the six doublets being considered, and re-analysing it per doublet was a third
 * of the sim's runtime for no information at all.
 */
function bestPlacement(
  s: State,
  d: Demesne,
  a: Analysis,
  baseline: number,
  doub: Doublet,
  tier: Tier,
  budget: { left: number },
): Best {
  const spots = legalPlacements(d, s.mode, s.side, doub);
  if (spots.length === 0) return { value: 0, cell: 0, rot: 0, gain: 0, legal: false };
  const scored = spots.map((sp) => ({ sp, g: gainOf(a, d, s.side, doub, (sp / ROTS) | 0, sp % ROTS) }));
  // Fixed enumeration order already; a stable sort by gain keeps ties resolving on that index.
  scored.sort((x, y) => y.g - x.g || x.sp - y.sp);

  if (!tier.structural) {
    const top = scored[0];
    return { value: top.g, cell: (top.sp / ROTS) | 0, rot: top.sp % ROTS, gain: top.g, legal: true };
  }

  const roundsLeft = s.rounds - s.round + 1;
  const doubletsLeft = roundsLeft - 1;
  const wide = Math.min(tier.wide, scored.length, Math.max(1, budget.left));
  let best: Best = { value: -Infinity, cell: 0, rot: 0, gain: 0, legal: true };
  for (let i = 0; i < wide; i++) {
    const sp = scored[i].sp;
    const cell = (sp / ROTS) | 0;
    const rot = sp % ROTS;
    const b = mate(s.side, cell, rot);
    const t = scratch(d);
    t.terr[cell] = doub.a.terrain;
    t.cor[cell] = doub.a.coronets;
    t.terr[b] = doub.b.terrain;
    t.cor[b] = doub.b.coronets;
    budget.left--;
    const v = structural(analyse(t, s.side), s, doubletsLeft, roundsLeft);
    if (v > best.value) best = { value: v, cell, rot, gain: scored[i].g, legal: true };
  }
  // Report the value as a DELTA against doing nothing, so it is comparable across doublets.
  return { ...best, value: best.value - baseline };
}

/** Deterministic sixteenths, from the log. The one stochastic tier must not touch Math.random. */
function wobble(s: State, seat: number): number {
  let h = 0x811c9dc5 ^ (s.seed >>> 0);
  h = Math.imul(h ^ s.log.length, 0x01000193) >>> 0;
  h = Math.imul(h ^ (seat + 1), 0x01000193) >>> 0;
  h = Math.imul(h ^ s.round, 0x01000193) >>> 0;
  return h & 15;
}

/**
 * Choose the move for whichever seat is to play. Pure: same state and tier, same move, every time
 * and on every device.
 */
export function chooseMove(s: State, tier: Tier): number {
  const seat = seatToPlay(s);
  if (seat < 0 || s.over) return -1;
  if (isBurnTurn(s)) return chooseBurn(s, seat);
  const d = s.boards[seat];
  const budget = { left: tier.nodeCap };
  const open = openPicks(s);
  const roundsLeft = s.rounds - s.round + 1;

  const mine = analyse(d, s.side);
  const baseline = tier.structural ? structural(mine, s, roundsLeft - 1, roundsLeft) : 0;
  const cands = open.map((i) => {
    const n = s.reveal[i];
    const doub = doubletOf(n) as Doublet;
    const best = bestPlacement(s, d, mine, baseline, doub, tier, budget);
    return { i, n, doub, best };
  });

  // The tempo price. rank is the doublet's position among what is still on offer, so paying it is
  // paying for a later slot next round — and on the final round there IS no next round, so it goes
  // to zero and the last claim is a pure grab.
  const byNumber = cands.slice().sort((a, b) => a.n - b.n);
  const rankOf = new Map<number, number>();
  byNumber.forEach((c, i) => rankOf.set(c.n, i));
  const values = cands.map((c) => c.best.value);
  const spread = Math.max(2, Math.max(...values) - Math.min(...values));
  const tempo = (tier.orderScale * (roundsLeft - 1)) / Math.max(1, s.rounds - 1);

  // What each doublet would be worth to the best-placed rival — all boards are public, so this is
  // free information the bot would be silly to ignore.
  const denial = new Map<number, number>();
  if (tier.denial > 0 && s.players > 1) {
    // Rival boards are analysed once for the whole turn, not once per doublet considered.
    const rivals: Array<{ p: number; a: Analysis }> = [];
    for (let p = 0; p < s.players; p++) if (p !== seat) rivals.push({ p, a: analyse(s.boards[p], s.side) });
    for (const c of cands) {
      let worst = 0;
      for (const r of rivals) {
        let g = 0;
        for (const sp of legalPlacements(s.boards[r.p], s.mode, s.side, c.doub)) {
          const v = gainOf(r.a, s.boards[r.p], s.side, c.doub, (sp / ROTS) | 0, sp % ROTS);
          if (v > g) g = v;
        }
        if (g > worst) worst = g;
      }
      denial.set(c.n, worst);
    }
  }

  let bestIdx = 0;
  let bestScore = -Infinity;
  let runnerUp = 0;
  let runnerScore = -Infinity;
  const denom = Math.max(1, cands.length - 1);
  cands.forEach((c, idx) => {
    const rank = (rankOf.get(c.n) as number) / denom;
    let v = c.best.value - tempo * rank * spread + tier.denial * (denial.get(c.n) ?? 0);
    // holt: going last buys the burn. `veto` is what that is worth, and the sweep says zero.
    if (s.mode.burn) v += tier.veto * tempo * rank * spread;
    if (v > bestScore) {
      runnerUp = bestIdx;
      runnerScore = bestScore;
      bestScore = v;
      bestIdx = idx;
    } else if (v > runnerScore) {
      runnerScore = v;
      runnerUp = idx;
    }
  });
  // The weakest tier plays the genuine second-best claim now and then, which is what a person new to
  // the game does. Drawn from a hash of the log, never a clock or Math.random — this runs on the
  // autoplay path where two peers must reach the same move.
  if (tier.noise > 0 && cands.length > 1 && wobble(s, seat) < tier.noise) bestIdx = runnerUp;

  const chosen = cands[bestIdx];
  return encodeMove({
    round: s.round,
    slot: s.slot,
    pick: chosen.n,
    cell: chosen.best.legal ? chosen.best.cell : 0,
    rot: chosen.best.legal ? chosen.best.rot : 0,
    discard: !chosen.best.legal,
  });
}

/**
 * holt's opening burn: strike out whatever the table would gain most from and I would gain least.
 *
 * The subtraction is the whole point. Burning the row's fattest doublet is wrong if it is fattest
 * for ME — I claim last, and last is still a claim.
 */
function chooseBurn(s: State, seat: number): number {
  const open = openPicks(s);
  const mineA = analyse(s.boards[seat], s.side);
  const rivals: Array<{ p: number; a: Analysis }> = [];
  for (let p = 0; p < s.players; p++) if (p !== seat) rivals.push({ p, a: analyse(s.boards[p], s.side) });

  const bestFor = (p: number, a: Analysis, doub: Doublet): number => {
    let g = 0;
    for (const sp of legalPlacements(s.boards[p], s.mode, s.side, doub)) {
      const v = gainOf(a, s.boards[p], s.side, doub, (sp / ROTS) | 0, sp % ROTS);
      if (v > g) g = v;
    }
    return g;
  };

  let bestIdx = open[0];
  let bestVal = -Infinity;
  for (const i of open) {
    const doub = doubletOf(s.reveal[i]) as Doublet;
    let theirs = 0;
    for (const r of rivals) {
      const g = bestFor(r.p, r.a, doub);
      if (g > theirs) theirs = g;
    }
    const v = theirs - 0.85 * bestFor(seat, mineA, doub);
    // Ties go to the higher number: it is the one that would otherwise have carried into the next
    // row, so burning it denies twice.
    if (v > bestVal || (v === bestVal && s.reveal[i] > s.reveal[bestIdx])) {
      bestVal = v;
      bestIdx = i;
    }
  }
  return encodeMove({ round: s.round, slot: s.slot, pick: s.reveal[bestIdx] });
}

/** A control arm for the balance sim: identical, but blind to pick order. Never shipped as a tier. */
export const tempoBlind = (t: Tier): Tier => ({ ...t, id: `${t.id}-blind`, orderScale: 0 });
